/**
 * Player watchdog decision logic.
 *
 * Extracted from `Player.tsx` so the two decisions the 1.5 s watchdog makes on every
 * tick can be unit-tested without mounting the whole player: (1) whether a fault it
 * just observed is *new* and therefore worth logging, and (2) whether the platform
 * needs `srcObject` cleared before a stream is (re)attached.
 *
 * Nothing here performs I/O or touches the DOM — it is pure decision logic. The
 * side effects (logging, assigning `srcObject`, retrying `play()`) stay in the
 * component, so gating a log can never gate a recovery action.
 */

/** The persistent conditions the 1.5 s watchdog reports on. */
export type PlayerWatchdogFault =
  /** A video track in the current stream has `readyState === 'ended'`. */
  | 'videoTrackEnded'
  /** The element lost its stream while the stream still carries video tracks. */
  | 'videoSrcObjectNull'
  /** The element is paused while the stream is meant to be visible. */
  | 'videoPausedWhileVisible';

/**
 * Latches watchdog faults so a persistent condition is logged once per *transition*
 * into it rather than once per tick.
 *
 * The latch is deliberately not permanent: once the condition clears, the fault is
 * re-armed and a recurrence logs again. That keeps the diagnostic a support engineer
 * relies on while removing the 1.5 s flood (and its retained argument object graphs)
 * from a phone that is already struggling.
 */
export class PlayerWatchdogFaultLatch {
  private readonly latched = new Set<PlayerWatchdogFault>();

  /**
   * Record this tick's observation of `fault` and report whether it should be logged.
   *
   * @returns `true` only on the tick that transitions the fault from clear to faulted.
   */
  public observe(fault: PlayerWatchdogFault, isFaulted: boolean): boolean {
    if (!isFaulted) {
      this.latched.delete(fault);
      return false;
    }
    if (this.latched.has(fault)) return false;
    this.latched.add(fault);
    return true;
  }

  /** True while `fault` is being suppressed because it was already reported. */
  public isLatched(fault: PlayerWatchdogFault): boolean {
    return this.latched.has(fault);
  }

  /** Re-arm every fault, e.g. when the stream is replaced wholesale. */
  public reset(): void {
    this.latched.clear();
  }
}

/**
 * Whether the video element must have `srcObject` set to `null` before a stream is
 * assigned to it.
 *
 * Both mobile platforms need it: iOS Safari otherwise keeps painting the previous
 * (now dead) stream, and Android Chrome can silently ignore the reassignment. The
 * primary attach path and the watchdog reattach path share this rule so neither
 * platform can lose the workaround the other has.
 */
export function requiresSrcObjectResetBeforeAttach(platform: {
  isIOS: boolean;
  isAndroid: boolean;
}): boolean {
  return platform.isIOS || platform.isAndroid;
}
